/**
 * Conversation Page
 * Real-time chat interface
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { chatAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import * as socketService from '../../services/socket';
import {
  ArrowLeft,
  Send,
  MoreVertical,
  User,
} from 'lucide-react';
import { format, isToday, isYesterday, isSameDay } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';
const UPLOADS_BASE_URL = API_BASE_URL.replace(/\/api\/?$/, '');

function ConversationPage() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [conversation, setConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [otherUserTyping, setOtherUserTyping] = useState(false);

  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // Load conversation and messages
  useEffect(() => {
    loadConversation();
  }, [conversationId]);

  // Join conversation room and setup socket listeners
  useEffect(() => {
    if (!conversationId) return;

    socketService.joinConversation(conversationId);

    // Listen for new messages
    const unsubMessage = socketService.on(socketService.EVENTS.NEW_MESSAGE, (data) => {
      try {
        if (data?.conversation_id === conversationId && data?.message) {
          setMessages(prev => [...prev, data.message]);
          scrollToBottom();
        }
      } catch (error) {
        console.error('Error processing new message:', error);
      }
    });

    // Listen for typing indicators
    const unsubTyping = socketService.on(socketService.EVENTS.USER_TYPING, (data) => {
      try {
        if (data?.conversation_id === conversationId && data?.user_id !== user?.id) {
          setOtherUserTyping(true);
        }
      } catch (error) {
        console.error('Error processing typing indicator:', error);
      }
    });

    const unsubStopTyping = socketService.on(socketService.EVENTS.USER_STOPPED_TYPING, (data) => {
      try {
        if (data?.conversation_id === conversationId && data?.user_id !== user?.id) {
          setOtherUserTyping(false);
        }
      } catch (error) {
        console.error('Error processing stop typing:', error);
      }
    });

    return () => {
      socketService.leaveConversation(conversationId);
      unsubMessage();
      unsubTyping();
      unsubStopTyping();
    };
  }, [conversationId, user?.id]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const loadConversation = async () => {
    try {
      const [messagesResponse, conversationResponse] = await Promise.all([
        chatAPI.getMessages(conversationId),
        chatAPI.getConversation(conversationId),
      ]);

      // API returns { success: true, data: [...] } for messages
      setMessages(messagesResponse.data || []);

      // API returns { success: true, data: {...conversation, participants} }
      setConversation(conversationResponse.data);

      // Mark as read after loading
      chatAPI.markAsRead(conversationId).catch(console.error);
    } catch (error) {
      console.error('Failed to load conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleTyping = useCallback(() => {
    socketService.startTyping(conversationId);

    // Clear existing timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    // Stop typing after 2 seconds of inactivity
    typingTimeoutRef.current = setTimeout(() => {
      socketService.stopTyping(conversationId);
    }, 2000);
  }, [conversationId]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;

    const messageContent = newMessage.trim();
    setNewMessage('');
    setSending(true);

    // Stop typing indicator
    socketService.stopTyping(conversationId);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    try {
      const response = await chatAPI.sendMessage(conversationId, messageContent);
      // API returns { success: true, data: {...message} }
      setMessages(prev => [...prev, response.data]);
    } catch (error) {
      console.error('Failed to send message:', error);
      setNewMessage(messageContent); // Restore message on error
    } finally {
      setSending(false);
    }
  };

  const formatMessageDate = (dateString) => {
    const date = new Date(dateString);
    if (isToday(date)) return 'Today';
    if (isYesterday(date)) return 'Yesterday';
    return format(date, 'EEEE, MMMM d');
  };

  const formatMessageTime = (dateString) => {
    return format(new Date(dateString), 'h:mm a');
  };

  const getOtherUser = () => {
    if (conversation?.other_user) {
      return conversation.other_user;
    }
    // Find other user from participants array
    if (conversation?.participants) {
      const other = conversation.participants.find(p => p.id !== user?.id);
      if (other) return other;
    }
    return { first_name: 'User', last_name: '' };
  };

  const shouldShowDateSeparator = (currentMsg, prevMsg) => {
    if (!prevMsg) return true;
    return !isSameDay(new Date(currentMsg.sent_at), new Date(prevMsg.sent_at));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner w-8 h-8" />
      </div>
    );
  }

  const otherUser = getOtherUser();

  return (
    <div className="max-w-2xl mx-auto h-[calc(100vh-12rem)] flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between pb-4 border-b border-gray-200">
        <div className="flex items-center">
          <button
            onClick={() => navigate(-1)}
            className="p-2 text-navy-500 hover:text-navy-700 hover:bg-gray-100 rounded-lg mr-2"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
            {otherUser.profile_photo_url ? (
              <img
                src={otherUser.profile_photo_url.startsWith('http') ? otherUser.profile_photo_url : `${UPLOADS_BASE_URL}${otherUser.profile_photo_url}`}
                alt=""
                className="h-10 w-10 rounded-full object-cover"
              />
            ) : (
              <span className="text-primary-700 font-medium">
                {otherUser.first_name?.[0]}{otherUser.last_name?.[0]}
              </span>
            )}
          </div>
          <div className="ml-3">
            <p className="font-medium text-navy-900">
              {otherUser.first_name} {otherUser.last_name}
            </p>
            {otherUserTyping && (
              <p className="text-sm text-primary-600">Typing...</p>
            )}
          </div>
        </div>
        <button className="p-2 text-navy-500 hover:text-navy-700 hover:bg-gray-100 rounded-lg">
          <MoreVertical className="h-5 w-5" />
        </button>
      </div>

      {/* Messages */}
      <div className="chat-messages flex-1 py-4 space-y-4">
        {messages.length === 0 ? (
          <div className="text-center py-12">
            <User className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-navy-500">No messages yet</p>
            <p className="text-sm text-navy-400">Send a message to start the conversation</p>
          </div>
        ) : (
          messages.map((message, index) => {
            const isOwn = message.sender_id === user?.id;
            const showDate = shouldShowDateSeparator(message, messages[index - 1]);

            return (
              <React.Fragment key={message.id}>
                {showDate && (
                  <div className="flex justify-center my-4">
                    <span className="px-3 py-1 bg-gray-100 text-navy-500 text-xs rounded-full">
                      {formatMessageDate(message.sent_at)}
                    </span>
                  </div>
                )}

                <div className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[75%]`}>
                    <div className={`chat-bubble ${isOwn ? 'sent' : 'received'}`}>
                      {message.content}
                    </div>
                    <p className={`text-xs text-navy-400 mt-1 ${isOwn ? 'text-right' : ''}`}>
                      {formatMessageTime(message.sent_at)}
                    </p>
                  </div>
                </div>
              </React.Fragment>
            );
          })
        )}

        {/* Typing indicator */}
        {otherUserTyping && (
          <div className="flex justify-start">
            <div className="chat-bubble received">
              <div className="flex space-x-1">
                <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-navy-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="pt-4 border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <input
            type="text"
            value={newMessage}
            onChange={(e) => {
              setNewMessage(e.target.value);
              handleTyping();
            }}
            placeholder="Type a message..."
            className="input-field flex-1"
            disabled={sending}
          />
          <button
            type="submit"
            disabled={!newMessage.trim() || sending}
            className="btn-primary p-3 disabled:opacity-50"
          >
            <Send className="h-5 w-5" />
          </button>
        </div>
      </form>
    </div>
  );
}

export default ConversationPage;
